import { useState, useEffect } from 'react';
import { clientesApi } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { validarRutChileno } from '../lib/rut';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { TableSkeleton } from '../lib/skeletons';
import { toast } from '../hooks/use-toast';
import { Plus, Search, Users, Pencil, Trash2, X, Loader2, Save, Printer, FileSpreadsheet, RefreshCw, Database } from 'lucide-react';
import { exportToCSV, printTable } from '../lib/printUtils';
// Iter 307.b (Carlos) — formulario compartido con el diálogo "Crear cliente
// nuevo" de Proyectos/OTs (misma información, mismo registro).
import { ClienteFormFields, CLIENTE_INITIAL_FORM } from '../components/ClienteFormFields';

const initialFormData = CLIENTE_INITIAL_FORM;

export const ClientesList = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // Iter 291.b — catálogo de condiciones de pago (CP-*) desde `condiciones_pago`,
  // mismo endpoint que usa Proveedores para su selector de condición.
  const [condicionesPago, setCondicionesPago] = useState([]);
  // Iter 292.b (Carlos, feb-2026) — Importar clientes desde Alegra (tipo=client).
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // stats del dry_run
  const [importing, setImporting] = useState(false);

  // Iter 415 — Backfill del maestro desde los DTE emitidos por CHIRINO (33/34).
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillModo, setBackfillModo] = useState('solo_vacios');
  const [backfillBusy, setBackfillBusy] = useState(false);

  const correrBackfillPreview = async (modo = backfillModo) => {
    setBackfillBusy(true);
    try {
      const res = await clientesApi.backfillDte({ dry_run: true, modo });
      setBackfillPreview(res.data);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error consultando DTE', variant: 'destructive' });
    } finally {
      setBackfillBusy(false);
    }
  };

  const openBackfill = async () => {
    setBackfillOpen(true);
    setBackfillPreview(null);
    await correrBackfillPreview('solo_vacios');
  };

  const ejecutarBackfill = async () => {
    setBackfillBusy(true);
    try {
      const res = await clientesApi.backfillDte({ dry_run: false, modo: backfillModo });
      const s = res.data.stats;
      toast({ title: `Backfill aplicado: ${s.a_actualizar} cliente(s), ${s.campos} campo(s)` });
      setBackfillOpen(false);
      setBackfillPreview(null);
      await loadClientes();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al aplicar backfill', variant: 'destructive' });
    } finally {
      setBackfillBusy(false);
    }
  };

  const openImportDialog = async () => {
    setImportDialogOpen(true);
    setImportPreview(null);
    setImporting(true);
    try {
      const res = await clientesApi.importarAlegra({ dry_run: true });
      setImportPreview(res.data.stats);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error consultando Alegra', variant: 'destructive' });
      setImportDialogOpen(false);
    } finally {
      setImporting(false);
    }
  };

  const ejecutarImportAlegra = async () => {
    setImporting(true);
    try {
      const res = await clientesApi.importarAlegra({ dry_run: false });
      const s = res.data.stats;
      toast({ title: `Importación OK: ${s.creados} nuevos, ${s.actualizados} actualizados` });
      setImportDialogOpen(false);
      setImportPreview(null);
      await loadClientes();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al importar', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    loadClientes();
    // Cargar catálogo de condiciones de pago (mismo endpoint que Proveedores)
    api.get('/condiciones-pago-crud', { params: { solo_nuevas: true } })
      .then((r) => setCondicionesPago(r.data || []))
      .catch(() => setCondicionesPago([]));
  }, []);

  const loadClientes = async () => {
    try {
      const res = await clientesApi.getAll();
      setClientes(res.data);
    } catch (error) {

      toast({ title: 'Error al cargar los clientes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (cliente = null) => {
    if (cliente) {
      setEditingId(cliente.id);
      // Merge con initialFormData para asegurar que los flags nuevos
      // (require_oc_cliente, require_hes, condicion_pago_default) existan
      // incluso si el registro del cliente es antiguo y no los tiene.
      setFormData({ ...initialFormData, ...cliente });
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.razon_social) {
      toast({ title: 'La razón social es obligatoria', variant: 'destructive' });
      return;
    }
    if (formData.rut && formData.rut.trim() && !validarRutChileno(formData.rut)) {
      toast({ title: 'RUT inválido: revisa el dígito verificador (módulo 11)', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await clientesApi.update(editingId, formData);
        toast({ title: 'Cliente actualizado' });
      } else {
        await clientesApi.create(formData);
        toast({ title: 'Cliente creado' });
      }
      handleCloseForm();
      loadClientes();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await clientesApi.delete(id);
      toast({ title: 'Cliente eliminado' });
      loadClientes();
    } catch (_error) {
      toast({ title: 'Error al eliminar el cliente', variant: 'destructive' });
    } finally {
      setDeleteConfirm(null);
    }
  };

  const filteredClientes = clientes.filter((c) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.razon_social?.toLowerCase().includes(search) ||
      c.rut?.toLowerCase().includes(search) ||
      c.contacto?.toLowerCase().includes(search)
    );
  });

  // Carlos (Feb 2026): imprimir y exportar listado de clientes
  const handlePrintClientes = () => {
    if (!filteredClientes.length) {
      toast({ title: 'No hay clientes para imprimir', variant: 'destructive' });
      return;
    }
    printTable({
      title: 'Listado de Clientes',
      subtitle: `${filteredClientes.length} clientes${searchTerm ? ` · Filtro: "${searchTerm}"` : ''} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total clientes', value: clientes.length },
        { label: 'En este listado', value: filteredClientes.length },
      ],
      columns: [
        { key: 'razon_social', label: 'Razón Social' },
        { key: 'rut', label: 'RUT', mono: true, align: 'center' },
        { key: 'contacto', label: 'Contacto' },
        { key: 'telefono', label: 'Teléfono', mono: true },
        { key: 'email', label: 'Email' },
        { key: 'direccion', label: 'Dirección' },
      ],
      rows: filteredClientes,
    });
  };

  const handleExcelClientes = () => {
    if (!filteredClientes.length) {
      toast({ title: 'No hay clientes para exportar', variant: 'destructive' });
      return;
    }
    exportToCSV({
      filename: 'clientes',
      title: 'CHIRINO STEEL · Listado de Clientes',
      summary: [
        { label: 'Total en sistema', value: clientes.length },
        { label: 'Clientes en archivo', value: filteredClientes.length },
        { label: 'Filtro búsqueda', value: searchTerm || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'razon_social', label: 'Razón Social' },
        { key: 'rut', label: 'RUT' },
        { key: 'contacto', label: 'Contacto' },
        { key: 'telefono', label: 'Teléfono' },
        { key: 'email', label: 'Email' },
        { key: 'direccion', label: 'Dirección' },
      ],
      rows: filteredClientes,
    });
  };

  if (loading) {
    return <TableSkeleton rows={8} cols={5} />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clientes-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Clientes</h1>
          <p className="text-muted-foreground">{clientes.length} clientes registrados</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openImportDialog} className="text-blue-700 border-blue-300 hover:bg-blue-50" title="Importar clientes desde Alegra" data-testid="btn-importar-alegra">
            <RefreshCw className="w-4 h-4 mr-2" /> Importar desde Alegra
          </Button>
          <Button variant="outline" onClick={openBackfill} className="text-violet-700 border-violet-300 hover:bg-violet-50" title="Completar Giro/Comuna/Ciudad y más desde los DTE emitidos" data-testid="btn-backfill-dte">
            <Database className="w-4 h-4 mr-2" /> Completar desde DTE
          </Button>
          <Button variant="outline" onClick={handlePrintClientes} disabled={!filteredClientes.length} title="Imprimir listado" data-testid="btn-print-clientes">
            <Printer className="w-4 h-4 mr-2" /> Imprimir
          </Button>
          <Button variant="outline" onClick={handleExcelClientes} disabled={!filteredClientes.length} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-clientes">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button onClick={() => handleOpenForm()} data-testid="new-cliente-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Form Panel */}
      {showForm && (
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <CardTitle className="font-heading">
                {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={handleCloseForm}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <ClienteFormFields
                formData={formData}
                setFormData={setFormData}
                condicionesPago={condicionesPago}
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseForm}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-cliente-btn">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  {editingId ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Card className="border-2 border-destructive/50 bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">¿Eliminar cliente "{deleteConfirm.razon_social}"?</p>
                <p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>
                  Cancelar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(deleteConfirm.id)}>
                  Eliminar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por razón social, RUT, contacto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredClientes.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No se encontraron clientes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-heading">Razón Social</TableHead>
                    <TableHead className="font-heading">RUT</TableHead>
                    <TableHead className="font-heading hidden md:table-cell">Contacto</TableHead>
                    <TableHead className="font-heading hidden lg:table-cell">Teléfono</TableHead>
                    <TableHead className="font-heading hidden lg:table-cell">Email</TableHead>
                    <TableHead className="w-24">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredClientes.map((cliente) => (
                    <TableRow key={cliente.id} data-testid={`cliente-row-${cliente.razon_social}`}>
                      <TableCell className="font-medium">{cliente.razon_social}</TableCell>
                      <TableCell className="font-mono text-sm">{cliente.rut || '-'}</TableCell>
                      <TableCell className="hidden md:table-cell">{cliente.contacto || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{cliente.telefono || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">{cliente.email || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenForm(cliente)}
                            data-testid={`edit-cliente-${cliente.razon_social}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirm(cliente)}
                            data-testid={`delete-cliente-${cliente.razon_social}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Iter 292.b (Carlos, feb-2026) — Diálogo Importar desde Alegra (tipo=client) */}
      {importDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="import-alegra-dialog">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className={`w-5 h-5 ${importing ? 'animate-spin' : ''} text-blue-600`} />
                Importar clientes desde Alegra
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!importPreview ? (
                <p className="text-sm text-muted-foreground">Consultando contactos marcados como <b>Cliente</b> en Alegra…</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Vista previa (dry-run). Match por RUT — sólo se completan campos vacíos en registros existentes.
                  </p>
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1" data-testid="import-preview">
                    <div className="flex justify-between"><span>Leídos desde Alegra</span><b>{importPreview.leidos_alegra}</b></div>
                    <div className="flex justify-between text-emerald-700"><span>Nuevos a crear</span><b>{importPreview.creados}</b></div>
                    <div className="flex justify-between text-blue-700"><span>A actualizar (campos vacíos)</span><b>{importPreview.actualizados}</b></div>
                    <div className="flex justify-between text-muted-foreground"><span>Sin cambios</span><b>{importPreview.sin_cambios}</b></div>
                    <div className="flex justify-between text-amber-700"><span>Sin RUT / RUT inválido (skip)</span><b>{importPreview.sin_rut_skip}</b></div>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setImportDialogOpen(false); setImportPreview(null); }} disabled={importing} data-testid="btn-import-cancel">
                  Cancelar
                </Button>
                <Button
                  onClick={ejecutarImportAlegra}
                  disabled={!importPreview || importing || (importPreview.creados === 0 && importPreview.actualizados === 0)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="btn-import-confirm"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ejecutar importación'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Iter 415 — Diálogo Completar desde DTE (backfill Giro/Comuna/Ciudad/etc.) */}
      {backfillOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="backfill-dte-dialog">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className={`w-5 h-5 ${backfillBusy ? 'animate-pulse' : ''} text-violet-600`} />
                Completar Maestro desde los DTE emitidos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Toma los datos del receptor (razón social, giro, dirección, comuna, ciudad, contacto, correo)
                desde la <b>factura 33/34 más reciente</b> emitida por CHIRINO a cada cliente, cruzando por RUT.
                Siempre en vista previa: se aplica solo al confirmar.
              </p>
              <div className="flex items-center gap-4 text-sm">
                <span className="font-medium">Modo:</span>
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="backfill-modo-vacios">
                  <input type="radio" name="backfill-modo" checked={backfillModo === 'solo_vacios'}
                    onChange={() => { setBackfillModo('solo_vacios'); correrBackfillPreview('solo_vacios'); }} />
                  Rellenar solo campos vacíos
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="backfill-modo-sobrescribir">
                  <input type="radio" name="backfill-modo" checked={backfillModo === 'sobrescribir'}
                    onChange={() => { setBackfillModo('sobrescribir'); correrBackfillPreview('sobrescribir'); }} />
                  Sobrescribir con el DTE más reciente
                </label>
              </div>
              {backfillBusy && !backfillPreview ? (
                <div className="py-6 text-center text-muted-foreground"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />Analizando DTE…</div>
              ) : backfillPreview ? (
                <>
                  <div className="rounded-md border bg-muted/30 p-3 text-sm grid grid-cols-2 gap-x-6 gap-y-1" data-testid="backfill-preview-stats">
                    <div className="flex justify-between"><span>Clientes con DTE 33/34</span><b>{backfillPreview.stats.clientes_con_dte}</b></div>
                    <div className="flex justify-between text-violet-700"><span>A actualizar</span><b>{backfillPreview.stats.a_actualizar}</b></div>
                    <div className="flex justify-between text-muted-foreground"><span>Sin cambios</span><b>{backfillPreview.stats.sin_cambios}</b></div>
                    <div className="flex justify-between text-amber-700"><span>DTE sin cliente en Maestro</span><b>{backfillPreview.stats.sin_cliente_en_maestro}</b></div>
                    <div className="flex justify-between"><span>Campos a completar</span><b>{backfillPreview.stats.campos}</b></div>
                  </div>
                  {(backfillPreview.cambios || []).length > 0 && (
                    <div className="border rounded-md max-h-64 overflow-y-auto" data-testid="backfill-cambios">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 sticky top-0"><tr className="text-left">
                          <th className="px-2 py-1.5">Cliente</th><th className="px-2 py-1.5">Campo</th>
                          <th className="px-2 py-1.5">Antes</th><th className="px-2 py-1.5">Después</th>
                        </tr></thead>
                        <tbody>
                          {backfillPreview.cambios.flatMap((c) => c.campos.map((cp, i) => (
                            <tr key={`${c.cliente_id}-${cp.campo}`} className="border-t">
                              {i === 0 ? <td className="px-2 py-1.5 font-medium align-top" rowSpan={c.campos.length}>{c.razon_social}<div className="text-[10px] text-muted-foreground font-mono">{c.rut}</div></td> : null}
                              <td className="px-2 py-1.5">{cp.campo}{cp.fuente ? <span className="text-[10px] text-muted-foreground"> ({cp.fuente})</span> : ''}</td>
                              <td className="px-2 py-1.5 text-muted-foreground line-through">{cp.antes || '—'}</td>
                              <td className="px-2 py-1.5 text-emerald-700">{cp.despues}</td>
                            </tr>
                          )))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setBackfillOpen(false); setBackfillPreview(null); }} disabled={backfillBusy} data-testid="btn-backfill-cancel">Cancelar</Button>
                <Button onClick={ejecutarBackfill} disabled={backfillBusy || !backfillPreview || (backfillPreview?.stats?.a_actualizar || 0) === 0}
                  className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="btn-backfill-confirm">
                  {backfillBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar cambios'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};